import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/hooks/use-toast';
import { getUsers, updateUserRoles } from '@/lib/api-calls';
import { QueryKeys } from '@/lib/queries';
import { isCoachRole, isEdgeCoach, isEdgeCoachRole } from '@airreading/common';
import { UserRoleAssignmentOptions, UserRoleStatus, UserWithRolesAndProfiles } from '@airreading/common/data';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ReactNode, useState } from 'react';

export interface ManageEdgeCoachProps {
  trigger: ReactNode;
  user: UserWithRolesAndProfiles;
}

const edgeCoachRole = () => ({ role: 'edge_coach' as const, schoolId: null, status: UserRoleStatus.active });

export const ManageEdgeCoach = ({ trigger, user }: ManageEdgeCoachProps) => {
  const [open, setOpen] = useState(false);

  const { mutateAsync, isPending } = useMutation({
    mutationFn: updateUserRoles,
  });

  const { toast } = useToast();
  const queryClient = useQueryClient();

  const hasActiveTeacher = user.userRoles.some((r) => isCoachRole(r) && r.status === UserRoleStatus.active);
  const userIsEdgeCoachNow = isEdgeCoach(user);

  if (!hasActiveTeacher) {
    return null;
  }

  const onSubmit = () => {
    const updatedRoles: UserRoleAssignmentOptions = userIsEdgeCoachNow
      ? user.userRoles.filter((role) => !isEdgeCoachRole(role))
      : [...user.userRoles, edgeCoachRole()];

    return mutateAsync({
      userId: user.id,
      roles: updatedRoles.map(({ role, schoolId, status }) => ({ role, schoolId, status })),
    })
      .then(() => {
        setOpen(false);
        queryClient.invalidateQueries(getUsers());
        queryClient.invalidateQueries({ queryKey: QueryKeys.getUser(user.id) });
        const fullName = user.fullNameOrEmail;
        toast({
          title: userIsEdgeCoachNow
            ? `Edge Tutors role removed from ${fullName}`
            : `Edge Tutors role added to ${fullName}`,
        });
      })
      .catch(() => {
        toast({
          title: 'Edge Tutors role update failed',
        });
      });
  };

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>{trigger}</AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{userIsEdgeCoachNow ? 'Remove Edge tutors' : 'Add Edge tutors'}</AlertDialogTitle>
          <AlertDialogDescription>
            {userIsEdgeCoachNow
              ? `Remove ${user.fullNameOrEmail} from the Edge Tutors role. They will keep the Teacher role.`
              : `Add ${user.fullNameOrEmail} as an Edge Tutors. They will keep the Teacher role but will not have access to Payments or My availability.`}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction asChild>
            <Button
              loading={isPending}
              onClick={(e) => {
                e.preventDefault();
                onSubmit();
              }}
            >
              {userIsEdgeCoachNow ? 'Remove Edge tutors' : 'Add Edge tutors'}
            </Button>
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
